import '@babel/polyfill';
import { Theme } from 'core-lib/constants/setting';
import { Cookies } from 'core-lib/constants/storage';
import { MG } from 'core-lib/data/partner';
import {
  updateReduxRestoreState,
  updateTheme,
} from 'core-lib/reduxStore/setting';
import { NextWebVitalsMetric } from 'next/app';
import dynamic from 'next/dynamic';
import toast, { Toaster, useToasterStore } from 'react-hot-toast';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store } from 'src/common/reduxStore/store';
import 'swiper/swiper.min.css';
import 'ui-v2/styles/global.scss';
import 'ui/styles/app.scss';

import ActivityRecorder from 'core-lib/components/ActivityRecorder';
import AnalyticsScripts from 'core-lib/components/AnalyticsScripts';
import GenericUI from 'core-lib/components/GenericUI';
import InitStore from 'core-lib/components/InitStore';
import NativeAuthHandler from 'core-lib/components/NativeAuthHandler';
import NativeBackHandler from 'core-lib/components/NativeBackHandler';
import NetworkInterceptor from 'core-lib/components/NetworkInterceptor';
import QueuedEvents from 'core-lib/components/QueuedEvents';
import VisibilityHandling from 'core-lib/components/VisibilityHandling';
import { PAGE_LOAD_SIGNAL } from 'core-lib/constants/nativeBridge';
import { PrimarySource } from 'core-lib/constants/Source';
import { getCookieValue } from 'core-lib/utils/cookie';
import { passMessageToNative } from 'core-lib/utils/nativeBridge';
import { isParkPlusWebView } from 'core-lib/utils/source';
import { isServer, sendToGoogleAnalytics } from 'core-lib/utils/utils';
import { useEffect } from 'react';

const ImageViewer = dynamic(
  () => import('src/common/components/image_viewer/image_viewer')
);
const Feedback = dynamic(
  () => import('src/common/components/feedbackOld/feedback')
);

export function reportWebVitals(metric: NextWebVitalsMetric) {
  if (!isServer()) {
    if (process.env.ENV === 'production') {
      sendToGoogleAnalytics(metric, 'PWA');
    }
  }
}

function MyApp(props) {
  return (
    <Provider store={store}>
      <AppWrapper {...props} />
    </Provider>
  );
}

const AppWrapper = ({ Component, pageProps }) => {
  const dispatch = useDispatch();
  const layout = useSelector((state) => state.layout);
  const handleOnDone = () => {
    if (
      getCookieValue(Cookies.AppOrigin)?.toLowerCase() === MG.value ||
      getCookieValue(Cookies.PrimarySource)?.toLowerCase() ===
        PrimarySource.MGWebView
    ) {
      dispatch(updateTheme(Theme.MG));
    }
    dispatch(updateReduxRestoreState(true));
  };

  const { toasts } = useToasterStore();

  useEffect(() => {
    toasts
      .filter((t, i) => t.visible && i >= 3) // Only consider visible toasts
      .forEach((t) => toast.dismiss(t.id)); // Dismiss – Use toast.remove(t.id) removal without animation
  }, [toasts]);

  useEffect(() => {
    if (isParkPlusWebView()) {
      passMessageToNative(PAGE_LOAD_SIGNAL, null, '');
    }
  }, []);

  return (
    <div className="wrapper">
      <div className="root">
        <Component {...pageProps} />
        {layout.imageViewer.show && <ImageViewer />}
        {layout.feedbackPopup.show && <Feedback />}
        <InitStore onDone={handleOnDone} />
        <GenericUI />
        <VisibilityHandling />
        <NetworkInterceptor />
        <ActivityRecorder />
        <NativeAuthHandler />
        <NativeBackHandler />
        <QueuedEvents />
        <AnalyticsScripts />
        <Toaster
          position="bottom-center"
          gutter={4}
          toastOptions={{
            style: {
              zIndex: 99999,
            },
            success: {
              duration: 3500,
              className: 'cmn_toast_container',
              icon: null,
            },
            error: {
              duration: 3500,
              className: 'cmn_toast_container',
              icon: null,
            },
          }}
        />
      </div>
    </div>
  );
};
export default MyApp;
